import { useMemo } from "react";
import {
  makeStyles,
  tokens,
  Tooltip,
  Spinner,
  Button,
  Text,
  CounterBadge,
  Toaster,
  useToastController
} from "@fluentui/react-components";
import {
  PeopleRegular, CloudRegular, ArrowUp16Regular,
  ArrowDown16Regular,
  ArrowSort16Regular,
  AppsListRegular,
} from "@fluentui/react-icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useCustomers } from "./useCustomers";
import { formatDateTime } from "../adminUtil";
import GlobalTableSearch from "./GlobalTableSearch";
import AdminTopbar from "../AdminTopbar";
import EditAcsDialog from "./EditAcsDialog";
import TablePagination from "../TablePagination";

const useStyles = makeStyles({
  tableWrapper: {
    height: "calc(100vh - 58px)",
    display: "flex",
    flexDirection: "column",
  },
  container: {
    padding: "8px 6px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    background: tokens.colorNeutralBackground1,

    "& th, & td": {
      padding: "6px 6px",
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
      textAlign: "left",
    },

    "& thead th": {
      background: tokens.colorNeutralBackground2,
      fontWeight: 600,
    },
  },
  tableHeader: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: tokens.colorNeutralBackground2,
  },
  tableScroll: {
    flex: 1,
    overflowY: "auto",
  },
  actions: {
    display: "flex",
    gap: "6px",
  },
  center: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
    flexDirection: "column",
    gap: "10px",
  },
  topbar: {
    gridColumn: "2",
    gridRow: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: 'space-between',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    position: "sticky",
    top: '0px',
    zIndex: 1000,
  },
  globalSearch: {
    marginRight: "24px"
  },
  badge: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: "999px",
    fontSize: "12px",
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForegroundLink,
  },
  syncColumn: {
    maxWidth: "140px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  paginationWrapper: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground1,
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    padding: "0px 8px",
  },
});

const columnHelper = createColumnHelper();

export default function CustomersTable({ onNavigate }) {
  const {
    data,
    loading,
    error,
    totalCount,
    sorting,
    setSorting,
    globalFilter,
    setGlobalFilter,
    pagination,
    setPagination,
    refetch,
  } = useCustomers();
  const styles = useStyles();
  const toasterId = "global-toaster-customers";
  const { dispatchToast } = useToastController(toasterId);

  const columns = useMemo(
    () => [
      columnHelper.accessor("tenantName", {
        header: "Tenant Name",
        cell: (info) => <strong>{info.getValue()}</strong>,
      }),
      columnHelper.accessor("tenantId", {
        header: "Tenant ID",
        enableSorting: false,
      }),
      columnHelper.accessor("clientAppApproverUser", {
        header: "Client App Approver",
      }),
      columnHelper.accessor("syncAppApproverUser", {
        header: "Sync App Approver",
        cell: (info) => (
          <div className={styles.syncColumn}>
            {info.getValue()}
          </div>),
      }),
      columnHelper.accessor("tenantOnboardingDate", {
        header: "Onboarding Date (UTC)",
        cell: (info) => formatDateTime(info.getValue()),
      }),
      columnHelper.accessor("syncAppApproveDate", {
        header: "Sync App Date (UTC)",
        cell: (info) => formatDateTime(info.getValue()),
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        enableSorting: false,
        cell: ({ row }) => (
          <div className={styles.actions}>
            <Tooltip content={"View Users"} relationship="label">
              <Button
                size="small"
                appearance="transparent"
                icon={<PeopleRegular />}
                onClick={() => onNavigate?.("users", row.original)}
              />
            </Tooltip>
            <Tooltip content={"View Salesforce Orgs"} relationship="label">
              <Button
                size="small"
                appearance="transparent"
                icon={<CloudRegular />}
                onClick={() => onNavigate?.("salesforce", row.original)}
              />
            </Tooltip>
            <Tooltip content={"Manage Queues"} relationship="label">
              <Button
                size="small"
                appearance="transparent"
                icon={<AppsListRegular />}
                onClick={() => onNavigate?.("queues", row.original)}
              />
            </Tooltip>
            <EditAcsDialog
              dispatchToast={dispatchToast}
              row={row.original}
              onSuccess={() => refetch()}
            />
          </div>
        ),
      }),
    ],
    [onNavigate, refetch, styles, dispatchToast],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: Math.ceil(totalCount / pagination.pageSize) || 1,
    enableMultiSort: false,
  });

  const rows = table.getRowModel().rows;

  return (
    <>
      <Toaster toasterId={toasterId} />
      <header className={styles.topbar}>
        <AdminTopbar title={"Manage Customers"} titleExtra={
          <Tooltip content={globalFilter ? "Matching customers" : "Total customers"} relationship="label">
            {loading ? (
              <Spinner size="tiny" style={{ marginLeft: 4 }} />
            ) : (
              <CounterBadge
                className={styles.badge}
                count={totalCount}
                overflowCount={9999}
                showZero
              />
            )}
          </Tooltip>
        } />
        <div className={styles.globalSearch}>
          <GlobalTableSearch
            value={globalFilter}
            onChange={setGlobalFilter}
            placeholder="Search customers..."
            debounceMs={0}
          />
        </div>
      </header>

      <div className={styles.container}>
        <div className={styles.tableWrapper}>
          <div className={styles.tableScroll}>
            {loading ? (
              <div className={styles.center}>
                <Spinner size="large" />
                <Text>Loading customers...</Text>
              </div>
            ) : error ? (
              <div className={styles.center}>
                <Text>Failed to load customers ❌</Text>
              </div>
            ) : (
              <table className={styles.table}>
                <thead className={styles.tableHeader}>
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {hg.headers.map((header) => (
                        <th
                          key={header.id}
                          onClick={(e) =>
                            header.column.getToggleSortingHandler()?.(e)
                          }
                          style={{
                            cursor: header.column.getCanSort() ? "pointer" : "default",
                          }}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center" }}>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {header.column.getCanSort() && (
                              <Tooltip
                                content={
                                  header.column.getIsSorted()
                                    ? `Sorted ${header.column.getIsSorted() === "asc" ? "ascending" : "descending"}.`
                                    : "Click to sort."
                                }
                                relationship="label"
                              >
                                <span style={{ display: "inline-flex", alignItems: "center", marginLeft: 4 }}>
                                  {header.column.getIsSorted() === "asc" && <ArrowUp16Regular />}
                                  {header.column.getIsSorted() === "desc" && <ArrowDown16Regular />}
                                  {!header.column.getIsSorted() && <ArrowSort16Regular />}
                                </span>
                              </Tooltip>
                            )}
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {rows.length > 0 ? (
                    rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columns.length} style={{ padding: "32px 16px", textAlign: "center" }}>
                        <Text>No customers found.</Text>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Pagination ─────────────────────────────────────────── */}
          <div className={styles.paginationWrapper}>
            <TablePagination
              table={table}
              totalCount={totalCount}
              pagination={pagination}
              setPagination={setPagination}
              disabled={loading}
            />
          </div>
        </div>
      </div>
    </>
  );
}
